import helpers from '@/common/helpers';
import PlanFormulaModel from '@/common/models/plan-formula-model';

function renderRecurringInterval(recurringInterval) {
    if (!recurringInterval) {
        return ` one-time sale`;
    }

    const {length, unit: periodUnit, limit} = recurringInterval;

    const periodLabel = length > 1
        ? ` every ${length} ${helpers.pluralize(periodUnit, length)}`
        : ` every ${periodUnit}`;

    if (!limit) {
        return periodLabel;
    }

    return `${periodLabel} for ${limit} ${helpers.pluralize('period', limit)}`;
}

function renderBracketMargin(minQuantity, maxQuantity) {
    if (minQuantity) {
        return maxQuantity
            ? `${minQuantity} to ${maxQuantity}`
            : `${minQuantity} or more`;
    }

    return maxQuantity
        ? `up to maximum ${maxQuantity}`
        : ``;
}

function renderTier(tier, minQuantity, maxQuantity) {
    if (minQuantity) {
        const tieredStep = tier > 0 ? 'for the next' : 'for the first';

        return maxQuantity
            ? `${tieredStep} ${minQuantity} to ${maxQuantity}`
            : `${tieredStep} ${minQuantity} or more`;
    }

    return maxQuantity
        ? `up to maximum ${maxQuantity}`
        : ``;
}

function renderStairstepBracket(
    index,
    price,
    minQuantity,
    maxQuantity,
    period,
    productUnit,
) {
    const margins = renderBracketMargin(minQuantity, maxQuantity);

    return margins
        ? `${price} in total (not per ${productUnit}) when quantity is ${margins}${period}`
        : `${price} in total (not per ${productUnit})${period}`;
}

function renderTieredBracket(
    index,
    price,
    minQuantity,
    maxQuantity,
    period,
    productUnit,
) {
    const margins = renderTier(index, minQuantity, maxQuantity);

    return margins
        ? `${price} per ${productUnit} ${margins}${period}`
        : `${price} per ${productUnit}${period}`;
}

function renderVolumeBracket(
    index,
    price,
    minQuantity,
    maxQuantity,
    period,
    productUnit,
) {
    const margins = renderBracketMargin(minQuantity, maxQuantity);

    return margins
        ? `${price} per ${productUnit} when the total quantity is ${margins}${period}`
        : `${price} per ${productUnit}${period}`;
}

function renderSimplePricing(
    {
        currency,
        pricing,
        recurringInterval = null,
    },
) {
    const price = helpers.monetize(pricing.price, currency);
    const period = renderRecurringInterval(recurringInterval);

    return `${price}${period}`;
}

function renderBracketPrice(
    {
        currency,
        pricing,
        recurringInterval,
        productUnit,
    },
    bracketRenderer,
) {
    const period = renderRecurringInterval(recurringInterval);
    const startMargin = pricing.brackets.length > 1 ? 1 : null;

    const reducer = (messages, currentBracket, index, brackets) => {
        const price = helpers.monetize(currentBracket.price, currency);
        const {maxQuantity} = currentBracket;
        const minQuantity = index
            ? +(brackets[index - 1].maxQuantity) + 1
            : startMargin;

        messages.push(
            bracketRenderer(
                index,
                price,
                minQuantity,
                maxQuantity,
                period,
                productUnit,
            ),
        );

        return messages;
    };

    return pricing.brackets.reduce(reducer, []);
}

export default class PricingRenderer {
    static render(
        currency,
        pricing,
        recurringInterval = null,
        productUnit = 'unit',
    ) {
        switch (pricing.formula) {
            case PlanFormulaModel.Formulas.fixedFee:
            case PlanFormulaModel.Formulas.flatRate:
                return [renderSimplePricing({currency, pricing, recurringInterval})];
            case PlanFormulaModel.Formulas.stairstep:
                return renderBracketPrice(
                    {
                        currency,
                        pricing,
                        recurringInterval,
                        productUnit,
                    },
                    renderStairstepBracket,
                );
            case PlanFormulaModel.Formulas.tiered:
                return renderBracketPrice(
                    {
                        currency,
                        pricing,
                        recurringInterval,
                        productUnit,
                    },
                    renderTieredBracket,
                );
            case PlanFormulaModel.Formulas.volume:
                return renderBracketPrice(
                    {
                        currency,
                        pricing,
                        recurringInterval,
                        productUnit,
                    },
                    renderVolumeBracket,
                );
            default:
                throw new Error('Unsupported pricing formula');
        }
    }
}
